import React from 'react';

// ProjectRowContent — the single presentational row shared by every project
// task tree (the editable detail Outline and the read-only Inbox view). It owns
// the `.todo-row` skeleton (indent connectors, collapse chevron, status cell,
// title, trailing slot) and NOTHING mode-specific: no @dnd-kit hook, no
// completion hook, no add/delete/reorder logic. Each surface wraps it with its
// own drag adapter and feeds the variable parts in as slots, so the rendering
// can't drift between view and edit.
//
// Drag wiring lives in the wrappers, not here, because the two surfaces use
// incompatible @dnd-kit models (the Outline owns a SortableContext and uses
// useSortable for reorder/reparent; the Inbox rows are useDraggable sources
// inside TodoList's DndContext for schedule/claim, with ids namespaced
// `project:<id>` to avoid colliding with the same todo's Today row). The wrapper
// supplies `rowProps` (root attributes/handlers — empty for a row whose drag is
// owned by an outer wrapper) and `forwardRef` targets the `.todo-row` root for
// the sortable case.
export interface ProjectRowContentProps {
  /** State/preview modifier classes appended to the base `todo-row`. */
  rowClassName?: string;
  /** Root attributes/handlers (role, tabIndex, onClick, onKeyDown, style,
   *  data-*, …). The wrapper decides what lands here vs on an outer drag
   *  element; pass nothing for an inert inner row. */
  rowProps?: React.HTMLAttributes<HTMLDivElement> & Record<string, unknown>;
  connectors: boolean[];
  hasChildren: boolean;
  collapsed: boolean;
  todoId: string;
  onToggleCollapsed: (id: string) => void;
  /** Drag handle (edit only); omitted in read-only / schedule-drag modes. */
  grip?: React.ReactNode;
  /** Status affordance: toggle button (edit), read-only glyph (view parent),
   *  or completion checkbox (view leaf). */
  statusCell: React.ReactNode;
  title: string;
  /** Scheduled marker — part of the read-only base, shown identically in both
   *  modes (a scheduled task reads "Planned" whether you're viewing or editing). */
  planned?: boolean;
  /** Content rendered after the title + Planned badge, in the wrapper's own
   *  order — owner avatar (+ add/delete in edit). */
  afterTitle?: React.ReactNode;
  /** Absolutely-positioned reorder drop caret (edit only). */
  dropCaret?: React.ReactNode;
}

export const ProjectRowContent = React.forwardRef<HTMLDivElement, ProjectRowContentProps>(
  function ProjectRowContent(
    {
      rowClassName,
      rowProps,
      connectors,
      hasChildren,
      collapsed,
      todoId,
      onToggleCollapsed,
      grip,
      statusCell,
      title,
      planned,
      afterTitle,
      dropCaret,
    },
    ref,
  ) {
    return (
      <div ref={ref} className={`todo-row${rowClassName ? ` ${rowClassName}` : ''}`} {...rowProps}>
        {connectors.map((hasLine, i) => (
          <span
            key={i}
            className={`todo-row__indent${hasLine ? ' todo-row__indent--line' : ''}`}
            aria-hidden="true"
          />
        ))}
        {hasChildren ? (
          <button
            type="button"
            className="todo-row__chevron"
            aria-label={collapsed ? 'Expand subtasks' : 'Collapse subtasks'}
            aria-expanded={!collapsed}
            // Stop pointerdown so toggling collapse never starts a drag (reorder
            // in edit, schedule in view).
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              onToggleCollapsed(todoId);
            }}
          >
            {collapsed ? '▸' : '▾'}
          </button>
        ) : (
          <span className="todo-row__chevron todo-row__chevron--leaf" aria-hidden="true">
            ·
          </span>
        )}
        {grip}
        {statusCell}
        <span className="todo-row__title">{title}</span>
        {planned && (
          <span className="todo-row__planned-badge" title="Scheduled">
            Planned
          </span>
        )}
        {afterTitle}
        {dropCaret}
      </div>
    );
  },
);
